import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Cloud, RefreshCw, RotateCcw, Loader2, Search, FilterX,
  ChevronLeft, ChevronRight, Trash2, ExternalLink, AlertTriangle, CheckCircle2, Clock, PlayCircle,
} from "lucide-react";

const PAGE_SIZE = 50;

const STATUS_STYLES = {
  queued: { bg: "#FFF7ED", fg: "#9A3412", border: "#FED7AA", label: "In coda", Icon: Clock },
  running: { bg: "#EFF6FF", fg: "#1E40AF", border: "#BFDBFE", label: "In corso", Icon: PlayCircle },
  done: { bg: "#ECFDF5", fg: "#065F46", border: "#A7F3D0", label: "Completato", Icon: CheckCircle2 },
  failed: { bg: "#FEF2F2", fg: "#991B1B", border: "#FECACA", label: "Fallito", Icon: AlertTriangle },
};

const KIND_LABELS = {
  cliente_folders: "Crea cartelle cliente",
  cliente_sync: "Sync file cliente",
  commessa_folder: "Cartella commessa",
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || { bg: "#F3F4F6", fg: "#374151", border: "#E5E7EB", label: status, Icon: Clock };
  const Icon = s.Icon;
  return (
    <span
      data-testid={`job-status-${status}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border"
      style={{ background: s.bg, color: s.fg, borderColor: s.border }}
    >
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtDuration(startIso, endIso) {
  if (!startIso || !endIso) return "—";
  const ms = new Date(endIso) - new Date(startIso);
  if (ms < 0 || isNaN(ms)) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

export default function ArubaJobs() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [q, setQ] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [retryingAll, setRetryingAll] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get("/nextcloud/jobs/stats");
      setStats(r.data);
    } catch (e) {
      // silent
    }
  }, []);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = { skip: opts.skip ?? skip, limit: PAGE_SIZE };
      if (statusFilter) params.status = statusFilter;
      if (kindFilter) params.kind = kindFilter;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/nextcloud/jobs", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setSkip(r.data.skip ?? 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
    loadStats();
  }, [skip, statusFilter, kindFilter, q, loadStats]);

  useEffect(() => { load({ skip: 0 }); /* eslint-disable-next-line */ }, [statusFilter, kindFilter]);
  useEffect(() => { load({ skip: 0 }); loadStats(); /* eslint-disable-next-line */ }, []);

  // Auto-refresh every 4s while there are queued/running jobs
  useEffect(() => {
    if (!autoRefresh) return;
    const hasActive = (stats?.queued || 0) + (stats?.running || 0) > 0;
    if (!hasActive) return;
    const t = setInterval(() => { load(); }, 4000);
    return () => clearInterval(t);
  }, [autoRefresh, stats?.queued, stats?.running, load]);

  const onSearch = (e) => { e.preventDefault(); load({ skip: 0 }); };
  const resetFilters = () => { setStatusFilter(""); setKindFilter(""); setQ(""); setSkip(0); };

  const handleRetry = async (jobId) => {
    setRetrying(jobId);
    try {
      await api.post(`/nextcloud/jobs/${jobId}/retry`);
      toast.success("Job rimesso in coda");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRetrying(null);
    }
  };

  const handleRetryFailed = async () => {
    if (!window.confirm(`Rimettere in coda TUTTI i ${stats?.failed || 0} job falliti? L'operazione potrebbe essere lunga.`)) return;
    setRetryingAll(true);
    try {
      const r = await api.post("/nextcloud/jobs/retry-failed");
      toast.success(`${r.data.enqueued} job riaccodati`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRetryingAll(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm("Cancellare i job 'done' e 'failed' più vecchi di 30 giorni? L'azione è irreversibile.")) return;
    setCleaning(true);
    try {
      const r = await api.delete("/nextcloud/jobs/cleanup", { params: { older_than_days: 30, status: "done,failed" } });
      toast.success(`${r.data.deleted} job cancellati`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCleaning(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="space-y-6 p-6 max-w-[1400px] mx-auto" data-testid="page-aruba-jobs">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl" style={{ background: "rgba(27,94,32,0.08)" }}>
            <Cloud className="w-6 h-6" style={{ color: "var(--brand-green)" }} />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
              Aruba Drive · Job
            </h1>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Monitoraggio della coda di sincronizzazione · {stats?.workers || 0} worker attivi
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox" checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              data-testid="auto-refresh-toggle"
            />
            <span>Auto-refresh</span>
          </label>
          <button
            onClick={() => load()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium hover:bg-black/5"
            style={{ borderColor: "var(--border)" }}
            data-testid="btn-refresh"
            disabled={loading}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Aggiorna
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: "queued", label: "In coda", value: stats?.queued ?? "—" },
          { key: "running", label: "In corso", value: stats?.running ?? "—" },
          { key: "done", label: "Completati", value: stats?.done ?? "—" },
          { key: "failed", label: "Falliti", value: stats?.failed ?? "—" },
          { key: "total", label: "Totale storico", value: stats?.total ?? "—" },
        ].map((s) => {
          const st = STATUS_STYLES[s.key] || { bg: "#F9FAFB", fg: "#374151", border: "#E5E7EB" };
          return (
            <button
              key={s.key}
              onClick={() => setStatusFilter(s.key === "total" ? "" : s.key)}
              className="text-left rounded-xl border p-4 transition hover:shadow-sm"
              style={{
                borderColor: statusFilter === s.key ? st.fg : "var(--border)",
                background: statusFilter === s.key ? st.bg : "white",
              }}
              data-testid={`stat-card-${s.key}`}
            >
              <div className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: st.fg }}>
                {s.label}
              </div>
              <div className="text-2xl font-extrabold mt-1" style={{ color: st.fg, fontFamily: "Manrope" }}>
                {s.value}
              </div>
            </button>
          );
        })}
      </div>

      {/* Bulk actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border" style={{ borderColor: "var(--border)", background: "white" }}>
        <div className="text-sm" style={{ color: "var(--text-muted)" }}>
          Azioni admin (storico completo)
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleRetryFailed}
            disabled={retryingAll || !stats?.failed}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
            style={{ background: "#B45309" }}
            data-testid="btn-retry-all-failed"
            title="Rimetti in coda tutti i job falliti"
          >
            {retryingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            Retry {stats?.failed || 0} falliti
          </button>
          <button
            onClick={handleCleanup}
            disabled={cleaning}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border disabled:opacity-50"
            style={{ borderColor: "var(--border)" }}
            data-testid="btn-cleanup-old"
          >
            {cleaning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Cleanup &gt; 30gg
          </button>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={onSearch} className="flex flex-wrap items-center gap-2 p-3 rounded-xl border" style={{ borderColor: "var(--border)", background: "white" }}>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per cliente, errore, kind…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: "var(--border)" }}
            data-testid="filter-q"
          />
        </div>
        <select
          value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm bg-white"
          style={{ borderColor: "var(--border)" }}
          data-testid="filter-kind"
        >
          <option value="">Tutti i tipi</option>
          <option value="cliente_folders">Crea cartelle cliente</option>
          <option value="cliente_sync">Sync file cliente</option>
          <option value="commessa_folder">Cartella commessa</option>
        </select>
        <select
          value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm bg-white"
          style={{ borderColor: "var(--border)" }}
          data-testid="filter-status"
        >
          <option value="">Tutti gli stati</option>
          <option value="queued">In coda</option>
          <option value="running">In corso</option>
          <option value="done">Completati</option>
          <option value="failed">Falliti</option>
        </select>
        <button type="submit" className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ background: "var(--brand-green)" }} data-testid="btn-apply-filters">
          Cerca
        </button>
        {(statusFilter || kindFilter || q) && (
          <button type="button" onClick={resetFilters} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--border)" }} data-testid="btn-reset-filters">
            <FilterX className="w-4 h-4" /> Reset
          </button>
        )}
      </form>

      {/* Table */}
      <div className="rounded-xl border overflow-hidden bg-white" style={{ borderColor: "var(--border)" }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Tipo</th>
                <th className="px-4 py-3 font-semibold">Target</th>
                <th className="px-4 py-3 font-semibold">Stato</th>
                <th className="px-4 py-3 font-semibold">Progresso</th>
                <th className="px-4 py-3 font-semibold">Creato</th>
                <th className="px-4 py-3 font-semibold">Durata</th>
                <th className="px-4 py-3 font-semibold">Errore</th>
                <th className="px-4 py-3 font-semibold">Utente</th>
                <th className="px-4 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline-block" /></td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">Nessun job trovato con i filtri attuali</td></tr>
              )}
              {items.map((j) => (
                <tr key={j.id} className="border-t hover:bg-gray-50/60" style={{ borderColor: "var(--border)" }} data-testid={`job-row-${j.id}`}>
                  <td className="px-4 py-3 font-medium">{KIND_LABELS[j.kind] || j.kind}</td>
                  <td className="px-4 py-3">
                    {j.link ? (
                      <Link to={j.link} className="inline-flex items-center gap-1 text-blue-700 hover:underline" data-testid={`job-target-link-${j.id}`}>
                        {j.label}
                        {j.target_ref ? <span className="text-gray-500 ml-1">({j.target_ref})</span> : null}
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    ) : (j.label || j.target_id?.slice(0, 8))}
                  </td>
                  <td className="px-4 py-3"><StatusPill status={j.status} /></td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {j.total > 0 ? (
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                          <div
                            className="h-full"
                            style={{
                              width: `${Math.min(100, Math.round(((j.progress || 0) / j.total) * 100))}%`,
                              background: STATUS_STYLES[j.status]?.fg || "#1B5E20",
                            }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">{j.progress || 0}/{j.total}</span>
                      </div>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(j.created_at)}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDuration(j.started_at, j.finished_at)}</td>
                  <td className="px-4 py-3 max-w-[300px]">
                    {j.error ? (
                      <span className="block truncate text-red-700" title={j.error}>{j.error}</span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{j.user_name || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {(j.status === "failed" || j.status === "done") && (
                      <button
                        onClick={() => handleRetry(j.id)}
                        disabled={retrying === j.id}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium hover:bg-black/5 disabled:opacity-50"
                        style={{ borderColor: "var(--border)" }}
                        data-testid={`btn-retry-${j.id}`}
                        title="Rimetti in coda"
                      >
                        {retrying === j.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                        Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: "var(--border)" }}>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            {total === 0 ? "Nessun risultato" : `${skip + 1}–${Math.min(skip + items.length, total)} di ${total}`}
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={skip <= 0 || loading}
              onClick={() => { const ns = Math.max(0, skip - PAGE_SIZE); setSkip(ns); load({ skip: ns }); }}
              className="p-1.5 rounded-md border disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
              data-testid="btn-page-prev"
            ><ChevronLeft className="w-4 h-4" /></button>
            <div className="text-xs font-medium">Pagina {currentPage} / {totalPages}</div>
            <button
              disabled={skip + items.length >= total || loading}
              onClick={() => { const ns = skip + PAGE_SIZE; setSkip(ns); load({ skip: ns }); }}
              className="p-1.5 rounded-md border disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
              data-testid="btn-page-next"
            ><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
